import type { IRealValue } from "../data-object";

import { UnitsClient } from ".";
import type { UnitConversion } from "./units";

function convertToStandardValue<RealValueType extends IRealValue>(
  realValue: RealValueType,
  unitConversion: UnitConversion,
): RealValueType {
  const convert = (value: number) =>
    value * unitConversion.scale + unitConversion.translate;

  switch (realValue.type) {
    case "nominal_real": {
      return {
        ...realValue,
        nominal: convert(Number(realValue.nominal)),
        units: unitConversion.short_standard_units,
      };
    }

    case "normal_real": {
      return {
        ...realValue,
        mean: convert(realValue.mean),
        std: realValue.std * unitConversion.scale,
        units: unitConversion.short_standard_units,
      };
    }

    case "uniform_real": {
      return {
        ...realValue,
        lower_bound: convert(realValue.lower_bound),
        upper_bound: convert(realValue.upper_bound),
        units: unitConversion.short_standard_units,
      };
    }
  }
}

function convertToUserValue<RealValueType extends IRealValue>(
  realValue: RealValueType,
  unitConversion: UnitConversion,
): RealValueType {
  const convert = (value: number) =>
    (value - unitConversion.translate) / unitConversion.scale;

  switch (realValue.type) {
    case "nominal_real": {
      return {
        ...realValue,
        nominal: convert(Number(realValue.nominal)),
        units: unitConversion.short_user_units,
      };
    }

    case "normal_real": {
      return {
        ...realValue,
        mean: convert(realValue.mean),
        std: realValue.std / unitConversion.scale,
        units: unitConversion.short_user_units,
      };
    }

    case "uniform_real": {
      return {
        ...realValue,
        lower_bound: convert(realValue.lower_bound),
        upper_bound: convert(realValue.upper_bound),
        units: unitConversion.short_user_units,
      };
    }
  }
}

/**
 * We're converting between 2 units that may not be the SI standard units.
 * We must first convert into the SI units, then back out of it.
 * If the second conversion is not provided, assume conversion to the SI units.
 *
 * This *Sync variant of the convert function requires that you provide a
 * conversion map that is pre-populated with the units necessary to perform
 * the conversion.
 *
 * The async variant is more convenient in that it will fetch the needed
 * conversions for you, but will only work where you can await its response.
 */
export function convertRealValueSync<RealValueType extends IRealValue>(
  realValue: RealValueType,
  targetUnits: string,
  unitConversionMap: Partial<Record<string, UnitConversion>>,
): RealValueType {
  if (!realValue.units || realValue.units === targetUnits) {
    return realValue;
  }

  const fromUnits = unitConversionMap[realValue.units];
  if (!fromUnits) {
    throw new Error("from units were not provided in the conversion map");
  }

  const toUnits = unitConversionMap[targetUnits];
  if (!toUnits) {
    throw new Error("to units were not provided in the conversion map");
  }

  const standardValue = convertToStandardValue(realValue, fromUnits);
  return convertToUserValue(standardValue, toUnits);
}

export async function convertRealValue<RealValueType extends IRealValue>(
  realValue: RealValueType,
  targetUnits: string,
) {
  if (!realValue.units || realValue.units === targetUnits) {
    return realValue;
  }

  return convertRealValueSync(
    realValue,
    targetUnits,
    await UnitsClient.getInstance().getUnitConversionMap([
      realValue.units,
      targetUnits,
    ]),
  );
}
